import { isChunkLoadFailureMessage } from '@/lib/client/chunk-load-reload';

export function exceptionMessageFromProperties(
  props: Record<string, unknown> | undefined,
): string {
  if (!props) {
    return '';
  }
  if (typeof props.$exception_message === 'string' && props.$exception_message) {
    return props.$exception_message;
  }
  if (Array.isArray(props.$exception_values)) {
    for (const value of props.$exception_values) {
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  }
  const list = props.$exception_list;
  if (Array.isArray(list)) {
    for (const entry of list) {
      if (entry && typeof entry === 'object' && typeof (entry as { value?: unknown }).value === 'string') {
        const value = (entry as { value: string }).value;
        if (value.trim()) {
          return value;
        }
      }
    }
  }
  return '';
}

function frameSourceText(frame: unknown): string {
  if (!frame || typeof frame !== 'object') {
    return '';
  }
  const record = frame as Record<string, unknown>;
  const parts: string[] = [];
  for (const key of ['filename', 'module', 'function', 'abs_path', 'source']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      parts.push(value);
    }
  }
  const stacktrace = record.stacktrace;
  if (stacktrace && typeof stacktrace === 'object') {
    const frames = (stacktrace as { frames?: unknown }).frames;
    if (Array.isArray(frames)) {
      for (const nestedFrame of frames) {
        parts.push(frameSourceText(nestedFrame));
      }
    }
  }
  return parts.join(' ');
}

export function exceptionSourcesText(props: Record<string, unknown> | undefined): string {
  if (!props) {
    return '';
  }
  const parts: string[] = [];
  if (typeof props.$exception_source === 'string') {
    parts.push(props.$exception_source);
  }
  const list = props.$exception_list;
  if (Array.isArray(list)) {
    for (const entry of list) {
      parts.push(frameSourceText(entry));
    }
  }
  return parts.join(' ');
}

function exceptionListEntries(props: Record<string, unknown> | undefined): unknown[] {
  const list = props?.$exception_list;
  return Array.isArray(list) ? list : [];
}

function exceptionListTypes(props: Record<string, unknown> | undefined): string[] {
  const types: string[] = [];
  if (Array.isArray(props?.$exception_types)) {
    for (const type of props.$exception_types) {
      if (typeof type === 'string' && type.trim()) {
        types.push(type.toLowerCase());
      }
    }
  }
  for (const entry of exceptionListEntries(props)) {
    if (entry && typeof entry === 'object' && typeof (entry as { type?: unknown }).type === 'string') {
      const type = (entry as { type: string }).type.trim();
      if (type) {
        types.push(type.toLowerCase());
      }
    }
  }
  return types;
}

function collectStackFrames(entry: unknown): unknown[] {
  if (!entry || typeof entry !== 'object') {
    return [];
  }
  const record = entry as Record<string, unknown>;
  const stacktrace = record.stacktrace;
  if (!stacktrace || typeof stacktrace !== 'object') {
    return [];
  }
  const frames = (stacktrace as { frames?: unknown }).frames;
  return Array.isArray(frames) ? frames : [];
}

/** True when any in-app frame references Reach app code (not third-party Clerk CDN). */
export function hasReachInAppStackFrame(props: Record<string, unknown> | undefined): boolean {
  for (const entry of exceptionListEntries(props)) {
    for (const frame of collectStackFrames(entry)) {
      if (!frame || typeof frame !== 'object') {
        continue;
      }
      const record = frame as Record<string, unknown>;
      if (record.in_app !== true) {
        continue;
      }
      const sourceText = [
        record.source,
        record.filename,
        record.module,
        record.abs_path,
      ]
        .filter((value): value is string => typeof value === 'string')
        .join(' ');
      const lower = sourceText.toLowerCase();
      if (!lower) {
        continue;
      }
      if (lower.includes('clerk.kindway.ai') || lower.includes('@clerk/')) {
        continue;
      }
      if (
        lower.includes('components/') ||
        lower.includes('lib/') ||
        lower.includes('app/') ||
        lower.includes('turbopack://')
      ) {
        return true;
      }
    }
  }
  return false;
}

function isClerkSourceText(text: string): boolean {
  const lower = text.toLowerCase();
  return lower.includes('clerk.kindway.ai') || lower.includes('@clerk/');
}

function isClerkAuthTransitionMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('no session was found with id') ||
    lower.includes('not a member of the organization') ||
    lower.includes('you are signed out')
  );
}

function isAuthFlowUrl(url: string): boolean {
  const lower = url.toLowerCase();
  return (
    lower.includes('/sign-in') ||
    lower.includes('/sign-up') ||
    lower.includes('/onboarding') ||
    lower.includes('/post-sign')
  );
}

export function shouldDropPostHogException(props: Record<string, unknown> | undefined): boolean {
  const message = exceptionMessageFromProperties(props);
  const lower = message.toLowerCase();
  const currentUrl = typeof props?.$current_url === 'string' ? props.$current_url : '';
  if (lower.includes('jwt expired')) {
    return true;
  }
  if (message.startsWith('Event captured as exception') || message.startsWith('ProgressEvent captured as exception')) {
    return true;
  }
  if (
    lower.includes("unexpected token '<'") &&
    lower.includes('not valid json')
  ) {
    return true;
  }
  if (
    lower.includes('failed to fetch') &&
    lower.includes('/api/chats') &&
    lower.includes('unexpected response')
  ) {
    return true;
  }
  // Instagram / in-app browsers: third-party scripts touch webkit.messageHandlers without a guard.
  if (lower.includes('webkit.messagehandlers')) {
    return true;
  }
  // PostHog session replay extension network errors are telemetry noise, not app bugs.
  if (lower === 'failed to fetch' && exceptionSourcesText(props).includes('replay/external')) {
    return true;
  }
  // Stale deploy chunk loads are recovered by ChunkLoadReloadScript; drop telemetry noise.
  if (isChunkLoadFailureMessage(message) && message.includes('/_next/static/chunks/')) {
    return true;
  }
  const sources = exceptionSourcesText(props);
  // Clerk CDN load failures and chunk timeouts are third-party/network noise.
  if (
    lower.includes('failed_to_load_clerk_js') ||
    lower.includes('failed_to_load_clerk_ui') ||
    lower.includes('failed to load clerk js') ||
    lower.includes('failed to load clerk ui') ||
    (lower.includes('loading chunk') && isClerkSourceText(sources))
  ) {
    return true;
  }
  // Clerk iframe/worker bridge noise when the host does not expose postMessage.
  if (
    lower.includes('postmessage is not a function') &&
    (isClerkSourceText(sources) || !hasReachInAppStackFrame(props))
  ) {
    return true;
  }
  if (lower.includes('clerkjs: network error') && isClerkSourceText(sources)) {
    return true;
  }
  if (lower.includes('securityerror') && lower.includes('operation is insecure') && isClerkSourceText(sources)) {
    return true;
  }
  // Cross-origin script errors and Android WebView bridge noise.
  if (lower === 'script error.' && !hasReachInAppStackFrame(props)) {
    return true;
  }
  if (lower.includes('java object is gone') && lower.includes('postmessage')) {
    return true;
  }
  // Browser extension content-script bridge noise (password managers, MS Editor, etc.).
  if (
    lower.includes('object not found matching id:') &&
    lower.includes('methodname:') &&
    !hasReachInAppStackFrame(props)
  ) {
    return true;
  }
  // Third-party DOM mutation noise (translation overlays, extensions) — react-dom removeChild without Reach frames.
  if (
    lower.includes("failed to execute 'removechild'") &&
    lower.includes('not a child of this node') &&
    !hasReachInAppStackFrame(props)
  ) {
    return true;
  }
  // Clerk session/org transition noise during auth and onboarding flows.
  if (isClerkAuthTransitionMessage(message)) {
    if (isClerkSourceText(sources) || isAuthFlowUrl(currentUrl) || !hasReachInAppStackFrame(props)) {
      return true;
    }
  }
  // PostHog autocapture network errors are telemetry noise, not app bugs.
  if (lower === 'failed to fetch' && sources.includes('autocapture-utils')) {
    return true;
  }
  // Generic fetch/network failures without Reach stack frames (Safari "Load failed").
  if (lower === 'load failed' && !hasReachInAppStackFrame(props)) {
    return true;
  }
  // Generic fetch failures without Reach stack frames (PostHog replay, embed network blips).
  if (lower === 'failed to fetch' && !hasReachInAppStackFrame(props)) {
    return true;
  }
  // Stale deploy server actions are recovered by ChunkLoadReloadScript; drop telemetry noise.
  if (
    exceptionListTypes(props).includes('unrecognizedactionerror') ||
    lower.includes('unrecognizedactionerror') ||
    (lower.includes('server action') && lower.includes('was not found on the server'))
  ) {
    return true;
  }
  return false;
}
